<docs>
An inline, in-page notice: coloured surface, optional icon + title, a message, up to a few CTAs and
an optional dismiss button. The module also exports the `AlertColor`, `AlertSize`, `AlertCtaPosition`
and `AlertCta` types. ZoneGlobalAlert is the full-bleed band at the top of the page (account-level
warnings: failed payment, deactivated org, demo data), a different surface, not a colour of this one;
transient success feedback belongs in a toast, not an alert, per the Figma guidance.

## Anatomy

- **Header**: optional row with the icon and the title, rendered only when there is a title.
- **Body**: the default slot, the message itself. With an icon and no title the icon moves onto this
  row, to the left of the copy, instead of sitting above it.
- **CTAs**: the `ctas` prop (or the `#ctas` slot), beside the body or under it (`ctaPosition`).
- **Dismiss**: optional × button, top/right.

## v-model

Optional, and about visibility, not a value. Leave it unbound and the alert hides itself when × is
clicked (what Zone::Alert's Alpine wrapper does). Bind it (`v-model="showAlert"`) and the parent owns
visibility: the alert then only emits.

## Notes

- The root is `w-full` and block-level: it fills whatever it is placed in. Constrain it from the
  outside (`class="max-w-md"`, a grid cell) rather than from a prop. A caller `class` is merged onto
  the root, not swapped for the computed classes.
- No Reka primitive is involved. Reka has no inline-alert/banner primitive (Toast is transient,
  AlertDialog is modal), and dismissal here is one boolean.
- A screen-reader-only severity word ("Info" / "Success" / "Warning" / "Critical") is announced
  ahead of the content, since colour and icon carry that meaning visually and nothing else does. It
  is dropped when `role` is null.
- The icon is `aria-hidden`: decoration on top of the text, never the message.
- The title renders as a `<p>`, not a heading. An alert is a live region, not a section of the
  document, so it should not land in the heading outline.
- Uncontrolled dismissal is one-way. The alert re-appears only on remount (or bind v-model and set
  it back to true).
- The `right` → stacked reflow is a viewport media query (`md`, 768px), not a container query. It is
  the same one Zone::Alert uses, so a migrated page reflows at the same point. An alert in a narrow
  box on a wide screen will not flip on its own: pass `cta-position="bottom"` there.

Figma: [component](https://www.figma.com/design/eOrg3dHaQFZ7VdCZCcomNC/-Zone-DS--Components-Specs?node-id=962-17131),
[sizing and spacing](https://www.figma.com/design/eOrg3dHaQFZ7VdCZCcomNC/-Zone-DS--Components-Specs?node-id=507-2191)
</docs>

<template>
  <div
    v-if="visible"
    :class="wrapperClasses"
    :role="role || undefined"
    data-testid="alert"
  >
    <span
      v-if="severity"
      class="sr-only"
      data-testid="alert-severity"
    >{{ severity }}</span>

    <div
      :class="contentClasses"
      data-testid="alert-content"
    >
      <div
        v-if="hasHeader"
        class="flex items-center gap-2"
        data-testid="alert-header"
      >
        <slot
          v-if="iconInHeader"
          name="icon"
        >
          <ZoneIcon
            v-if="icon"
            :icon="icon"
            size="lg"
            class="shrink-0"
            aria-hidden="true"
          />
        </slot>

        <p
          v-if="hasTitle"
          class="min-w-0 flex-1 text-md font-semibold"
          data-testid="alert-title"
        >
          <slot name="title">{{ title }}</slot>
        </p>
      </div>

      <div
        v-if="hasBody || showBottomCtas"
        class="flex flex-col gap-4"
      >
        <div
          v-if="hasBody"
          class="flex items-start gap-2"
          data-testid="alert-body-row"
        >
          <slot
            v-if="iconBesideBody"
            name="icon"
          >
            <ZoneIcon
              v-if="icon"
              :icon="icon"
              size="lg"
              class="shrink-0 leading-sm"
              aria-hidden="true"
            />
          </slot>

          <div
            class="min-w-0 flex-1 text-sm"
            data-testid="alert-body"
          >
            <slot />
          </div>
        </div>

        <div
          v-if="showBottomCtas"
          class="flex flex-wrap items-center gap-2"
          data-testid="alert-ctas"
        >
          <slot name="ctas">
            <ZoneButton
              v-for="(cta, index) in ctas"
              :key="cta.id ?? index"
              v-bind="ctaProps(cta)"
              @click="$emit('cta', cta, $event)"
            >{{ cta.label }}</ZoneButton>
          </slot>
        </div>
      </div>
    </div>

    <div
      v-if="showRightCtas || dismissible"
      class="flex shrink-0 items-center gap-2"
    >
      <div
        v-if="showRightCtas"
        class="flex flex-wrap items-center gap-2 md:justify-end"
        data-testid="alert-ctas"
      >
        <slot name="ctas">
          <ZoneButton
            v-for="(cta, index) in ctas"
            :key="cta.id ?? index"
            v-bind="ctaProps(cta)"
            @click="$emit('cta', cta, $event)"
          >{{ cta.label }}</ZoneButton>
        </slot>
      </div>

      <!--
        The corner placement lives on this wrapper, not on the button: ZoneButton's own `relative`
        is emitted after `absolute` in the stylesheet and would win, leaving the × in the flow.
      -->
      <div
        v-if="dismissible"
        :class="dismissClasses"
        data-testid="alert-dismiss-slot"
      >
        <ZoneIconButton
          icon="close"
          size="sm"
          variant="ghost"
          :color="ctaColor"
          :aria-label="t('dismiss')"
          data-testid="alert-dismiss"
          @click="handleDismiss"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, ref, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import ZoneIcon, { type IconName } from './Icon.vue';
import ZoneButton, { type ButtonColor, type ButtonVariant } from './Button.vue';
import ZoneIconButton from './IconButton.vue';

const COLORS = ['gray', 'primary', 'green', 'yellow', 'red', 'purple'] as const;

export type AlertColor = (typeof COLORS)[number];

const SIZES = ['base', 'sm'] as const;

export type AlertSize = (typeof SIZES)[number];

const CTA_POSITIONS = ['right', 'bottom'] as const;

export type AlertCtaPosition = (typeof CTA_POSITIONS)[number];

const ROLES = ['alert', 'status'] as const;

export type AlertRole = (typeof ROLES)[number];

export interface AlertCta {
  id?: string | number;
  label: string;
  variant?: ButtonVariant;
  color?: ButtonColor;
  href?: string;
  icon?: IconName;
  disabled?: boolean;
}

// Surface + border + text, straight off the Figma spec (node 962:17131). Note the border shade is
// NOT uniform: primary and red sit on 700, the softer three on 600. `purple` has no entry in the
// current Figma colour set — it is carried over from Zone::Alert so migrating pages keep parity.
const COLOR_CLASSES: Record<AlertColor, string> = {
  gray: 'bg-gray-50 border-gray-600 text-gray-800',
  primary: 'bg-primary-50 border-primary-700 text-primary-800',
  green: 'bg-green-50 border-green-600 text-green-800',
  yellow: 'bg-yellow-50 border-yellow-600 text-yellow-800',
  red: 'bg-red-50 border-red-700 text-red-800',
  purple: 'bg-purple-50 border-purple-700 text-purple-800',
};

// The CTA (and ×) colour that lands on the alert's own border shade — ZoneButton's `outline:*`
// classes happen to use exactly those shades, which is why an alert-coloured button reads as part
// of the same surface. Ported from Zone::Alert::BUTTON_COLOR_MAPPING.
const CTA_COLORS: Record<AlertColor, ButtonColor> = {
  gray: 'gray',
  primary: 'primary_alt',
  green: 'green',
  yellow: 'yellow',
  red: 'red',
  purple: 'purple',
};

// Colour and icon carry the severity visually; this is the same information for a screen reader.
// Ported from Zone::Alert::VARIANTS_ARIA_LABEL.
const SEVERITY_LABELS: Record<AlertColor, string> = {
  gray: 'Info',
  primary: 'Info',
  purple: 'Info',
  green: 'Success',
  yellow: 'Warning',
  red: 'Critical',
};

// Figma "Layout and spacing": the alert pads 12px at Base and 8px at Small, and that gap between
// the header and the body is the only other thing the size changes.
const PADDING_CLASSES: Record<AlertSize, string> = { base: 'p-3', sm: 'p-2' };
const CONTENT_GAP_CLASSES: Record<AlertSize, string> = { base: 'gap-2', sm: 'gap-1' };

// Below `md` the right-CTA layout reflows to a column, and the × leaves the flow for the corner
// so it does not eat a whole row of its own — exactly what Zone::Alert's `close_button_classes`
// does. The offsets match the alert's own padding at each size, and the clearance is the ×
// (a 20px ZoneIconButton) plus that same padding, so wrapped copy never runs under it.
const DISMISS_STACKED_CLASSES: Record<AlertSize, string> = {
  base: 'absolute right-3 top-3 md:static',
  sm: 'absolute right-2 top-2 md:static',
};
const DISMISS_CLEARANCE_CLASSES: Record<AlertSize, string> = {
  base: 'pr-8 md:pr-0',
  sm: 'pr-7 md:pr-0',
};
</script>

<script setup lang="ts">
defineOptions({ name: 'ZoneAlert' });

const { t } = useI18n();

const props = defineProps({
  /**
   * Visibility, not a value. Leave it unbound and the alert hides itself when × is clicked; bind it
   * and the parent owns visibility (the alert then only emits).
   */
  modelValue: {
    type: Boolean as PropType<boolean | null>,
    default: null,
  },
  /**
   * Figma calls `primary` "Blue". Gray is neutral, primary a highlighted neutral, green positive,
   * yellow caution, red urgent. `purple` is carried over from Zone::Alert and is not in the current
   * Figma colour set.
   */
  color: {
    type: String as PropType<AlertColor>,
    default: 'primary',
    validator: (v: string) => COLORS.includes(v as AlertColor),
  },
  /**
   * Padding only (12px vs 8px) and a tighter header/body gap; the type scale is identical. Mirrors
   * Figma's Base/Small and Zone::Alert's `:base`/`:sm`, hence not the sm|md|lg scale the other Zone
   * components use.
   */
  size: {
    type: String as PropType<AlertSize>,
    default: 'base',
    validator: (v: string) => SIZES.includes(v as AlertSize),
  },
  /**
   * Header text (see the `#title` slot for markup). With neither this nor `#title` nor an icon, no
   * header row is rendered.
   */
  title: {
    type: String,
    default: '',
  },
  /**
   * Zone icon name (see ZoneIcon). No per-colour default: pick one that fits the message. It sits
   * left of the title when there is one, and left of the body when there isn't, never on a row of
   * its own above the copy.
   */
  icon: {
    type: String as PropType<IconName>,
    default: null,
  },
  /**
   * Up to three per Figma. Each renders a 40px (`md`) ZoneButton, `outline` in the alert's own colour
   * unless the item overrides `variant`/`color`.
   */
  ctas: {
    type: Array as PropType<AlertCta[]>,
    default: () => [],
  },
  /**
   * `right` puts the CTAs on one row beside the body and reflows to the stacked layout below `md`
   * (768px), so the copy never gets squeezed into a narrow column. `bottom` stacks them under the
   * body at every width: use it in a container that is narrow on a wide viewport (a card, a
   * sidebar), which a media query cannot see. Either way the CTAs wrap onto more rows as needed.
   */
  ctaPosition: {
    type: String as PropType<AlertCtaPosition>,
    default: 'right',
    validator: (v: string) => CTA_POSITIONS.includes(v as AlertCtaPosition),
  },
  /** Render the × button. */
  dismissible: {
    type: Boolean,
    default: false,
  },
  /**
   * Live-region politeness: `alert` is assertive, `status` the polite one for non-urgent info, and
   * `null` renders no role at all for a purely static banner.
   */
  role: {
    type: String as PropType<AlertRole | null>,
    default: 'alert',
    validator: (v: string | null) => v === null || ROLES.includes(v as AlertRole),
  },
  /**
   * Overrides the screen-reader-only severity word derived from `color` ("Info" / "Success" /
   * "Warning" / "Critical"); `''` suppresses it.
   */
  severityLabel: {
    type: String,
    default: null,
  },
});

const emit = defineEmits<{
  /** Same moment as `dismiss`, for the v-model form. */
  'update:modelValue': [visible: boolean];
  /** × was clicked. */
  dismiss: [];
  /**
   * A `ctas` item was clicked. Not fired for a disabled item, and not fired for the `#ctas` slot
   * (wire those up yourself).
   */
  cta: [cta: AlertCta, event: MouseEvent];
}>();

const slots = defineSlots<{
  /**
   * The message. Arbitrary markup, not just text: a paragraph and a list, a definition list, a
   * link, an inline form. Styled `text-sm` and given the remaining width of its row.
   */
  default?(): any;
  /** Overrides the `title` prop. */
  title?(): any;
  /** Overrides the `icon` prop (e.g. an avatar), in whichever of the two positions the icon would have taken. */
  icon?(): any;
  /**
   * Replaces the buttons rendered from `ctas` with your own markup, positioned by `ctaPosition`
   * just the same. Zone::Alert's `with_cta_section` equivalent.
   */
  ctas?(): any;
}>();

const selfDismissed = ref(false);

const visible = computed<boolean>(() => (props.modelValue === null ? !selfDismissed.value : props.modelValue));

const hasTitle = computed<boolean>(() => Boolean(props.title || slots.title));

const hasIcon = computed<boolean>(() => Boolean(props.icon || slots.icon));

const hasBody = computed<boolean>(() => Boolean(slots.default));

// With a title the icon leads the header row. With body copy and NO title there is no header
// for it to lead, so it joins the body's own row rather than sitting on a line above it.
const iconBesideBody = computed<boolean>(() => hasIcon.value && !hasTitle.value && hasBody.value);

const iconInHeader = computed<boolean>(() => hasIcon.value && !iconBesideBody.value);

const hasHeader = computed<boolean>(() => Boolean(hasTitle.value || iconInHeader.value));

const hasCtas = computed<boolean>(() => Boolean(props.ctas.length || slots.ctas));

const showRightCtas = computed<boolean>(() => hasCtas.value && props.ctaPosition === 'right');

const showBottomCtas = computed<boolean>(() => hasCtas.value && props.ctaPosition === 'bottom');

const ctaColor = computed<ButtonColor>(() => CTA_COLORS[props.color]);

const severity = computed<string>(() => {
  if (!props.role) {return '';}

  return props.severityLabel === null ? SEVERITY_LABELS[props.color] : props.severityLabel;
});

const wrapperClasses = computed<string>(() => [
  // `relative` is the positioning context the × falls back on in the stacked layout.
  'relative flex w-full rounded-lg border',
  PADDING_CLASSES[props.size],
  // 12px between the body column and whatever sits right of it — except in the bottom-CTA
  // layout, where Figma nests the × one level deeper and the gap there is 8px.
  showBottomCtas.value ? 'gap-2' : 'gap-3',
  // CTAs beside the body hold that row only while there is width for it: under `md` the
  // alert reflows to a column so the copy gets the full width and the CTAs drop below it,
  // the same flip Zone::Alert makes. With just a × (or nothing) on the right it top-aligns,
  // so the × stays level with the first line of a wrapped message.
  showRightCtas.value ? 'flex-col md:flex-row md:items-center' : 'items-start',
  COLOR_CLASSES[props.color],
].join(' '));

const contentClasses = computed<string>(() => [
  'flex min-w-0 flex-1 flex-col',
  CONTENT_GAP_CLASSES[props.size],
  // Stacked, the × sits in the corner over this column — keep the copy clear of it.
  showRightCtas.value && props.dismissible ? DISMISS_CLEARANCE_CLASSES[props.size] : '',
].filter(Boolean).join(' '));

const dismissClasses = computed<string>(() => ['flex shrink-0', showRightCtas.value ? DISMISS_STACKED_CLASSES[props.size] : ''].filter(Boolean).join(' '));

function ctaProps(cta: AlertCta) {
  return {
    variant: cta.variant || 'outline',
    color: cta.color || ctaColor.value,
    href: cta.href,
    leadingIcon: cta.icon,
    disabled: Boolean(cta.disabled),
    // Figma and Zone::Alert both draw the CTA at 40px: ZoneButton's `md`, not the 32px `sm`.
    size: 'md' as const,
  };
}

function handleDismiss() {
  if (props.modelValue === null) {selfDismissed.value = true;}

  emit('dismiss');
  emit('update:modelValue', false);
}
</script>

<i18n lang="json">
{
  "en": {
    "dismiss": "Dismiss"
  }
}
</i18n>
